/** @fileoverview Calls the hosted app routes on the gateway with the app pass: the app definition, data queries, and checklist and timeline rows. */

import type { RuntimeRequest } from '../services/componentStateApi';
import { buildDataSourceQueryBody } from '../services/fabrikApi';
import {
  getAppRuntimeFieldOptionsPath,
  getAppRuntimeFieldValuesPath,
  getAppRuntimeQueryPath,
  getAppRuntimeWorkdayQueryPath,
} from '../services/fabrikQueryPaths';
import { readRuntimeResponse } from '../services/runtimeResponse';
import type { FabrikViewer } from '../types/identity';
import type {
  FabrikAppDefinition,
  FabrikResolvedAppearance,
  FabrikFieldOptions,
  FabrikFieldValuesPage,
  FabrikFieldValuesRequest,
  FabrikListQueryOptions,
  FabrikSystemDataSource,
} from '../types/apps';
import { readAppPass } from './appPass';
import { getCurrentGatewayUrl } from './hostConfig';

export type PublishedApp = {
  appId: string;
  slug: string;
  name: string;
  tenantName: string;
  viewer?: FabrikViewer;
  version: number | null;
  mode: 'live' | 'preview';
  definition: FabrikAppDefinition;
  appearance: FabrikResolvedAppearance;
};

export type PublishedAppResult =
  | { kind: 'ok'; app: PublishedApp }
  | { kind: 'signedOut' }
  | { kind: 'notPublished' }
  | { kind: 'notFound' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

const GENERIC_ERROR = 'Unable to load this app. Please try again.';
const DATA_ERROR = 'Unable to load the data. Please try again.';
const NOT_PUBLISHED_PATTERN = /hasn'?t been published|not_published|not published/i;

/** Reads the response body as text, or '' when it cannot be read. */
const readBodyText = async (response: Response) => {
  try {
    return await response.text();
  } catch {
    return '';
  }
};

/**
 * Reads the server's message from an error body, or '' when the body has none.
 * @param bodyText response body as text
 */
const readRefusalText = (bodyText: string) => {
  try {
    const { msg } = JSON.parse(bodyText) as { msg?: unknown };
    return typeof msg === 'string' ? msg : '';
  } catch {
    return '';
  }
};

/** Fetches the published app with the pass as a Bearer token and maps the answer to a small result. */
export const fetchPublishedApp = async (): Promise<PublishedAppResult> => {
  const gatewayUrl = getCurrentGatewayUrl().replace(/\/$/, '');
  const pass = readAppPass();
  if (!gatewayUrl) {
    return { kind: 'error', message: GENERIC_ERROR };
  }
  if (!pass) {
    return { kind: 'signedOut' };
  }
  try {
    const response = await fetch(`${gatewayUrl}/api/app/fabrik/app-runtime/app`, {
      method: 'GET',
      credentials: 'omit',
      headers: { Authorization: `Bearer ${pass}` },
    });
    if (response.status === 401) {
      return { kind: 'signedOut' };
    }
    if (response.status === 403) {
      return { kind: 'forbidden' };
    }
    const text = await readBodyText(response);
    if (response.status === 404) {
      return NOT_PUBLISHED_PATTERN.test(text) ? { kind: 'notPublished' } : { kind: 'notFound' };
    }
    if (!response.ok) {
      return { kind: 'error', message: GENERIC_ERROR };
    }
    const app = JSON.parse(text) as PublishedApp;
    app.mode = app.mode === 'preview' ? 'preview' : 'live';
    if (!app?.definition || !Array.isArray(app.definition.screens)) {
      return { kind: 'error', message: GENERIC_ERROR };
    }
    return { kind: 'ok', app };
  } catch {
    return { kind: 'error', message: GENERIC_ERROR };
  }
};

/** Thrown when the app pass is no longer accepted. */
export class AppSignedOutError extends Error {
  constructor() {
    super('Your session has ended.');
    this.name = 'AppSignedOutError';
  }
}

/**
 * Sends a JSON request to a hosted app route with the app pass and maps failures to plain messages.
 * Answers with JSON, nothing for 204, or a file (see `readRuntimeResponse`).
 * Throws AppSignedOutError when the pass is missing or no longer accepted.
 * @param request gateway path, method, body, abort signal, and the messages for "not found" and
 *   other failures
 */
export const sendAppRuntimeRequest = async ({
  path,
  method,
  body,
  signal,
  messages,
}: RuntimeRequest): Promise<unknown> => {
  const gatewayUrl = getCurrentGatewayUrl().replace(/\/$/, '');
  const pass = readAppPass();
  if (!gatewayUrl) {
    throw new Error(messages.failed);
  }
  if (!pass) {
    throw new AppSignedOutError();
  }
  const response = await fetch(`${gatewayUrl}/api${path}`, {
    method,
    credentials: 'omit',
    headers: { Authorization: `Bearer ${pass}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (response.status === 401) {
    throw new AppSignedOutError();
  }
  if (response.status === 403) {
    /* An action refused for the viewer's user groups explains itself; other refusals stay generic. */
    const refusal = path.includes('/actions/') ? readRefusalText(await readBodyText(response)) : '';
    throw new Error(refusal || 'You do not have access to this data.');
  }
  if (response.status === 404) {
    throw new Error(messages.notFound);
  }
  if (response.status === 422 && path.endsWith('/links')) {
    /* A link that is not set up yet explains what is missing. */
    const refusal = readRefusalText(await readBodyText(response));
    throw new Error(refusal || messages.failed);
  }
  if (!response.ok) {
    throw new Error(messages.failed);
  }
  return readRuntimeResponse(response);
};

/** Reads one page or the row count of a datasource through the hosted app route. */
export const queryAppDataSource = ({
  systemId,
  dataSource,
  options,
  signal,
}: {
  systemId: string;
  dataSource: FabrikSystemDataSource;
  options: FabrikListQueryOptions;
  signal?: AbortSignal;
}): Promise<unknown> =>
  sendAppRuntimeRequest({
    path:
      dataSource.type === 'workday'
        ? getAppRuntimeWorkdayQueryPath(systemId)
        : getAppRuntimeQueryPath(systemId),
    method: 'POST',
    body: buildDataSourceQueryBody(dataSource, options),
    signal,
    messages: { notFound: "This data couldn't be found.", failed: DATA_ERROR },
  });

/**
 * Reads, in a hosted app, the values some fields of an entity can take, with labels.
 * @param request system, entity and field names
 */
export const readAppFieldOptions = async ({
  systemId,
  entity,
  fields,
}: {
  systemId: string;
  entity: string;
  fields: string[];
}): Promise<FabrikFieldOptions> =>
  (
    (await sendAppRuntimeRequest({
      path: getAppRuntimeFieldOptionsPath(systemId),
      method: 'POST',
      body: { entity, fields },
      messages: { notFound: "This data couldn't be found.", failed: DATA_ERROR },
    })) as { fields: FabrikFieldOptions }
  ).fields;

/**
 * Reads, in a hosted app, a search page of one system field's values, or the labels of some codes.
 * @param request system, entity, field, and either the search with its page or the codes
 */
export const readAppFieldValues = async ({
  systemId,
  ...body
}: FabrikFieldValuesRequest): Promise<FabrikFieldValuesPage> =>
  (await sendAppRuntimeRequest({
    path: getAppRuntimeFieldValuesPath(systemId),
    method: 'POST',
    body,
    messages: { notFound: "This data couldn't be found.", failed: DATA_ERROR },
  })) as FabrikFieldValuesPage;
